import { useState } from 'react';
import Button from '../../../shared/components/Button';
import { Input } from '../../../shared/components/Input';
import { Modal } from '../../../shared/components/Modal';
import { Select } from '../../../shared/components/Select';
import { Toggle } from '../../../shared/components/Toggle';
import {
  useCreateReferralCode,
  useReferralPrograms,
  useUpdateReferralCode,
} from '../hooks/useReferrals';
import { REFERRER_TYPE_LABELS, ReferrerType, type ReferralCode } from '../types';

interface Props {
  /** Absent = create mode. */
  code?: ReferralCode;
  onClose: () => void;
}

const DEFAULT_PROGRAM = '';
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * A readable, unambiguous code from the referrer's name — e.g. "Ada Okafor" → "ADA-7K3Q". No
 * 0/O/1/I so it survives being read out on a call. The server is the uniqueness authority (409);
 * this only saves typing.
 */
const suggestCode = (name: string) => {
  const stem = name.trim().split(/\s+/)[0]?.replace(/[^a-z]/gi, '').slice(0, 6).toUpperCase() || 'TCS';
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const suffix = Array.from(
    { length: 4 },
    () => alphabet[Math.floor(Math.random() * alphabet.length)],
  ).join('');
  return `${stem}-${suffix}`;
};

/**
 * Create or edit a referral code. The referrer lives ON the code (name, email, type) — there is
 * no separate referrer entity, which is also how osprey-api blocks self-referral: it compares the
 * code's referrerEmail with the referred contact's email.
 *
 * Code and referrer type are fixed once created (UpdateReferralCodeDto omits both), so they are
 * create-only here. Leaving the program empty means "use the platform default reward rule".
 */
export function CodeFormModal({ code, onClose }: Props) {
  const isEdit = !!code;
  const create = useCreateReferralCode();
  const update = useUpdateReferralCode();
  const { data: programs = [] } = useReferralPrograms();

  const [value, setValue] = useState(code?.code ?? '');
  const [referrerType, setReferrerType] = useState<ReferrerType>(
    code?.referrerType ?? ReferrerType.MERCHANT,
  );
  const [referrerDisplayName, setReferrerDisplayName] = useState(code?.referrerDisplayName ?? '');
  const [referrerEmail, setReferrerEmail] = useState(code?.referrerEmail ?? '');
  const [referrerReferenceId, setReferrerReferenceId] = useState(code?.referrerReferenceId ?? '');
  const [rewardProgramId, setRewardProgramId] = useState(code?.rewardProgramId ?? DEFAULT_PROGRAM);
  const [isActive, setIsActive] = useState(code?.isActive ?? true);

  const isPending = create.isPending || update.isPending;
  const valid =
    (isEdit || value.trim().length >= 3) &&
    referrerDisplayName.trim().length > 0 &&
    EMAIL.test(referrerEmail.trim());

  const programOptions = [
    { value: DEFAULT_PROGRAM, title: 'Platform default rule' },
    ...programs
      .filter((p) => p.isActive || p.id === code?.rewardProgramId)
      .map((p) => ({ value: p.id, title: p.isDefault ? `${p.name} (default)` : p.name })),
  ];

  const submit = async () => {
    if (!valid) return;
    if (isEdit) {
      await update.mutateAsync({
        id: code.id,
        payload: {
          referrerDisplayName: referrerDisplayName.trim(),
          referrerEmail: referrerEmail.trim(),
          isActive,
          // UpdateReferralCodeDto's rewardProgramId is @IsUUID — it can re-point a code but not
          // clear it back to the default, so only send a real id.
          ...(rewardProgramId ? { rewardProgramId } : {}),
        },
      });
    } else {
      await create.mutateAsync({
        code: value.trim(),
        referrerType,
        referrerDisplayName: referrerDisplayName.trim(),
        referrerEmail: referrerEmail.trim(),
        ...(referrerReferenceId.trim() ? { referrerReferenceId: referrerReferenceId.trim() } : {}),
        ...(rewardProgramId ? { rewardProgramId } : {}),
      });
    }
    onClose();
  };

  return (
    <Modal isOpen onClose={onClose} size="md">
      <div className="p-6 space-y-5 max-h-[80vh] overflow-y-auto themed-scrollbar">
        <h2 className="text-[18px] font-semibold text-foreground">
          {isEdit ? `Edit ${code.code}` : 'New referral code'}
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            className="min-h-10 lg:min-h-0"
            label="Referrer name"
            value={referrerDisplayName}
            onChange={(e) => setReferrerDisplayName(e.target.value)}
            placeholder="Ada Okafor"
          />
          <Input
            className="min-h-10 lg:min-h-0"
            label="Referrer email"
            type="email"
            value={referrerEmail}
            onChange={(e) => setReferrerEmail(e.target.value)}
            placeholder="ada@brand.com"
          />
          {!isEdit && (
            <>
              <Select
                label="Referrer type"
                options={Object.entries(REFERRER_TYPE_LABELS).map(([v, title]) => ({ value: v, title }))}
                value={referrerType}
                onChange={(v) => setReferrerType(v as ReferrerType)}
              />
              <Input
                className="min-h-10 lg:min-h-0"
                label="Reference id (optional)"
                value={referrerReferenceId}
                onChange={(e) => setReferrerReferenceId(e.target.value)}
                placeholder="Merchant or staff id"
              />
            </>
          )}
        </div>

        {!isEdit && (
          <div className="flex items-end gap-3">
            <Input
              label="Code"
              className="flex-1 min-h-10 lg:min-h-0"
              value={value}
              onChange={(e) => setValue(e.target.value.toUpperCase())}
              placeholder="ADA-7K3Q"
            />
            <Button
              variant="outline"
              size="sm"
              className="h-[40px]"
              onClick={() => setValue(suggestCode(referrerDisplayName))}
            >
              Generate
            </Button>
          </div>
        )}

        <Select
          label="Reward rule"
          options={programOptions}
          value={rewardProgramId}
          onChange={setRewardProgramId}
        />

        {isEdit && (
          <Toggle
            label="Active — can be attributed to new contacts"
            checked={isActive}
            onChange={setIsActive}
          />
        )}

        <div className="flex justify-end gap-3">
          <Button variant="outline" size="sm" className="h-[40px]" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            className="h-[40px]"
            onClick={submit}
            disabled={!valid || isPending}
            loading={isPending}
          >
            {isEdit ? 'Save code' : 'Create code'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
